/**
 * Talkie v=2 outer authentication envelope — TypeScript counterpart
 * of `platform/crates/agentic-app-host/src/envelope/v2.rs`.
 *
 * Every bridge message that crosses the agentic-app iframe boundary
 * is wrapped in this envelope. The envelope authenticates the sender,
 * defends against replay attacks (±60s window), and pins the message
 * to a specific origin. `./bridge.ts` signs every outgoing message and
 * verifies every inbound one before it reads the payload (`./protocol.ts`).
 *
 * Wire format (canonical JSON, serialized for HMAC):
 *
 *   {
 *     v: 2,
 *     sig: "<base64-encoded HMAC-SHA256>",
 *     nonce: "<32-hex-char random>",
 *     timestamp_ms: <epoch milliseconds>,
 *     origin: "<scheme://host[:port]>",
 *     payload: <bridge message>
 *   }
 *
 * The HMAC is computed over the canonical serialization of
 *   `{ v, nonce, timestamp_ms, origin, payload }`
 * (i.e. the envelope MINUS the `sig` field). Top-level keys appear
 * in this fixed order via insertion-order JSON serialization; INNER
 * `payload` keys are sorted alphabetically by `sortKeysDeep` so the
 * canonical bytes are stable regardless of how the inner object was
 * constructed. This matches Rust's `serde_json::Value` (BTreeMap-
 * backed) default behaviour.
 *
 * The cross-language signature contract is locked by
 * `test/cross_lang_envelope_sig.test.ts` and its Rust counterpart
 * `tests/cross_lang_envelope_sig.rs` — both pin the SAME base64
 * string for the SAME inputs. Drift on either side fails both tests.
 */

import { hmac } from "@noble/hashes/hmac.js";
import { sha256 } from "@noble/hashes/sha2.js";

// ─────────────────────────────────────── Browser-portable helpers ──

/**
 * Browser-compatible random-bytes helper. Uses WebCrypto's
 * synchronous `crypto.getRandomValues` (available in modern browsers
 * AND Node ≥15 via the global `crypto` namespace). No `node:crypto`
 * import so the same code bundles for browser fixtures.
 */
function randomBytes(n: number): Uint8Array {
  const buf = new Uint8Array(n);
  if (typeof globalThis.crypto?.getRandomValues !== "function") {
    throw new Error(
      "envelope: globalThis.crypto.getRandomValues is unavailable; this build cannot generate random nonces",
    );
  }
  globalThis.crypto.getRandomValues(buf);
  return buf;
}

/**
 * RFC 4122 v4 id from `randomBytes`. Not `crypto.randomUUID`: that exists only in a secure context,
 * and a page served over plain http from a LAN address (a dev stack's fixtures) has
 * `getRandomValues` and nothing else.
 */
export function randomUuid(): string {
  const b = randomBytes(16);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = bytesToHex(b);
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/**
 * Browser-compatible base64 encoder for `Uint8Array` input. Uses
 * `btoa` with a binary-string bridge (works in browser + Node ≥16
 * since `btoa` is global there too).
 */
function bytesToBase64(bytes: Uint8Array): string {
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

/** Browser-compatible base64 decoder. */
function base64ToBytes(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/**
 * Constant-time equality check for two `Uint8Array`s. Browser-portable
 * replacement for `node:crypto`'s `timingSafeEqual`. Returns `false`
 * immediately on length mismatch (length is not secret), then ORs
 * xor'd byte pairs so total time is constant w.r.t. contents.
 */
function constantTimeEq(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a[i] ^ b[i];
  return diff === 0;
}

/** Bytes-to-hex helper. Browser-portable; lowercase per wire contract. */
function bytesToHex(bytes: Uint8Array): string {
  let out = "";
  for (let i = 0; i < bytes.length; i++) {
    out += bytes[i].toString(16).padStart(2, "0");
  }
  return out;
}

// ─────────────────────────────────────── Constants ──

/** Envelope version. Anything else is rejected at parse time. */
export const ENVELOPE_VERSION = 2 as const;

/**
 * Replay-window half-width. A message with `timestamp_ms` outside
 * `[now - WINDOW, now + WINDOW]` is rejected.
 *
 * 60 seconds covers reasonable clock skew between SPA / host while
 * keeping the replay window narrow enough that a captured envelope
 * has limited reuse value.
 */
export const REPLAY_WINDOW_MS = 60_000;

/** HMAC-SHA256 output length in bytes (constant). */
const HMAC_LEN_BYTES = 32;

// ─────────────────────────────────────── Types ──

/**
 * The on-wire envelope. `payload` is one bridge message (`./protocol.ts`),
 * opaque to the envelope.
 */
export interface OuterEnvelope {
  readonly v: typeof ENVELOPE_VERSION;
  readonly sig: string;
  readonly nonce: string;
  readonly timestamp_ms: number;
  readonly origin: string;
  readonly payload: unknown;
}

/**
 * Stable string labels for each envelope-rejection class. Mirrors
 * Rust's `error_code()` exactly so audit logs and dashboards can
 * partition by reason without parsing the formatted message.
 */
export type EnvelopeErrorCode =
  | "version_mismatch"
  | "structure_invalid"
  | "sig_mismatch"
  | "replay_window"
  | "replay_nonce"
  | "origin_mismatch";

export class EnvelopeError extends Error {
  constructor(
    public readonly code: EnvelopeErrorCode,
    message: string,
  ) {
    super(message);
    this.name = "EnvelopeError";
  }
}

/**
 * Per-connection replay-nonce cache (MCP audit H3). The ±window timestamp
 * check bounds a captured envelope to that window, but without nonce dedup an
 * attacker can replay a captured non-idempotent frame any number of times
 * INSIDE the window. This closes that: an `(origin, nonce)` pair seen once is
 * rejected if it reappears before its window expires.
 *
 * Mirrors the Rust host `NonceCache`
 * (`platform/crates/agentic-app-host/src/envelope/v2.rs`) exactly: an entry is
 * evicted once it ages past the FULL acceptance window (`2 * REPLAY_WINDOW_MS`
 * — a fresh envelope can arrive up to one window in the future, so an entry
 * must outlive its own timestamp by a window to still catch a replay near the
 * boundary). Eviction is amortized: every record sweeps expired entries, so the
 * cache is bounded by the distinct nonces seen within one window — no unbounded
 * growth. Scoped per origin so two peers can't evict/collide on each other's
 * nonces; one instance lives per receiving transport.
 */
export class NonceCache {
  /** key = `${origin}\u0000${nonce}` → seen-at epoch ms. */
  private readonly seen = new Map<string, number>();

  /**
   * Record a freshly-verified nonce, returning `false` if it was already
   * present within the window (a replay) and `true` if it is new. Sweeps
   * expired entries on every call so the map stays bounded. `nowMs` is the
   * verifier's clock; entries older than `2 * REPLAY_WINDOW_MS` are evicted.
   */
  checkAndRecord(origin: string, nonce: string, nowMs: number): boolean {
    const cutoff = nowMs - 2 * REPLAY_WINDOW_MS;
    // Deleting the current key during Map iteration is well-defined in JS.
    for (const [k, seenAt] of this.seen) {
      if (seenAt < cutoff) this.seen.delete(k);
    }
    const key = `${origin}\u0000${nonce}`;
    if (this.seen.has(key)) return false;
    this.seen.set(key, nowMs);
    return true;
  }

  /** Live entry count — introspection for tests, not a collection API. */
  get entryCount(): number {
    return this.seen.size;
  }
}

/** Inputs for verifying an inbound envelope. */
export interface VerifyOptions {
  /** Shared symmetric key. Same value on both signer and verifier. */
  readonly key: Uint8Array;
  /**
   * The exact `origin` string we expect the sender to claim. Mismatch
   * is a hard reject — closes the cross-origin spoof class.
   */
  readonly expectedOrigin: string;
  /**
   * Override the "now" clock (epoch ms). Defaults to `Date.now()`.
   * Tests pin this; production never sets it.
   */
  readonly nowMs?: number;
  /**
   * Per-connection replay-nonce cache. When provided, a `(origin, nonce)`
   * replayed within the window is rejected (`replay_nonce`) even though its
   * signature and timestamp are still valid (MCP audit H3). Omit to skip nonce
   * dedup (e.g. a one-shot verification with no session). The transport owns
   * one instance per peer connection.
   */
  readonly nonceCache?: NonceCache;
}

/** Inputs for signing an outbound envelope. */
export interface SignOptions {
  readonly key: Uint8Array;
  readonly origin: string;
  /** Override the "now" clock (epoch ms). Defaults to `Date.now()`. */
  readonly nowMs?: number;
  /**
   * Override the nonce. Defaults to 16 random bytes (32 hex chars).
   * Tests pin this for deterministic signatures; production never
   * sets it.
   */
  readonly nonceHex?: string;
}

// ─────────────────────────────────────── Canonical serialization ──

/**
 * Recursively sort the keys of every object in the value, returning
 * a structurally equivalent value with deterministic key order.
 * Arrays preserve their element order (order IS meaningful in JSON
 * arrays). Primitives pass through.
 *
 * Why: the Rust counterpart uses `serde_json::Value` which is
 * `BTreeMap`-backed and serializes object keys in alphabetical
 * order. `JSON.stringify` uses insertion order. To produce the same
 * canonical bytes on both sides regardless of how the inner payload
 * was constructed, we sort object keys recursively here. The Rust
 * side gets sorted keys "for free" via `serde_json`'s default.
 */
function sortKeysDeep(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(sortKeysDeep);
  }
  if (value !== null && typeof value === "object") {
    const obj = value as Record<string, unknown>;
    const sorted: Record<string, unknown> = {};
    for (const k of Object.keys(obj).sort()) {
      sorted[k] = sortKeysDeep(obj[k]);
    }
    return sorted;
  }
  return value;
}

/**
 * Serialize the envelope's authenticated fields (everything except
 * `sig`) into the exact byte sequence both signer and verifier feed
 * into HMAC.
 *
 * Top-level envelope key order is FIXED: `v`, `nonce`, `timestamp_ms`,
 * `origin`, `payload` — defined by the order in which we build the
 * object literal here. `JSON.stringify` honours insertion order; the
 * Rust side declares the same order via `#[derive(Serialize)]` on its
 * `SignableEnvelope` struct.
 *
 * INNER `payload` keys are sorted alphabetically via `sortKeysDeep`
 * to match Rust's `serde_json::Value` (BTreeMap-backed) default.
 */
function canonicalize(args: {
  v: number;
  nonce: string;
  timestamp_ms: number;
  origin: string;
  payload: unknown;
}): Uint8Array {
  const ordered = {
    v: args.v,
    nonce: args.nonce,
    timestamp_ms: args.timestamp_ms,
    origin: args.origin,
    payload: sortKeysDeep(args.payload),
  };
  return new TextEncoder().encode(JSON.stringify(ordered));
}

// ─────────────────────────────────────── Sign ──

/**
 * Wrap one bridge message in a signed v=2 outer envelope.
 *
 * Returns the envelope object — callers serialize it to JSON before
 * sending. The Transport adapter does both steps.
 *
 * Throws on a malformed `nonceHex` override (must be 32 lowercase
 * hex chars when provided).
 */
export function sign(payload: unknown, opts: SignOptions): OuterEnvelope {
  const nonce = opts.nonceHex ?? bytesToHex(randomBytes(16));
  if (!/^[0-9a-f]{32}$/.test(nonce)) {
    throw new Error(
      `envelope.sign: nonceHex must be 32 lowercase hex chars; got "${nonce}"`,
    );
  }
  const timestamp_ms = opts.nowMs ?? Date.now();
  const canonical = canonicalize({
    v: ENVELOPE_VERSION,
    nonce,
    timestamp_ms,
    origin: opts.origin,
    payload,
  });
  const sig = bytesToBase64(hmac(sha256, opts.key, canonical));
  return {
    v: ENVELOPE_VERSION,
    sig,
    nonce,
    timestamp_ms,
    origin: opts.origin,
    payload,
  };
}

// ─────────────────────────────────────── Verify ──

/**
 * Verify an inbound envelope and return its inner payload.
 *
 * Throws `EnvelopeError` with a specific `code` on any failure mode.
 * Callers should NOT swallow the error — every failure class is a
 * security-relevant audit event (replay attempt, key compromise,
 * cross-origin spoof, …).
 */
export function verify(envelope: unknown, opts: VerifyOptions): unknown {
  if (
    envelope === null ||
    typeof envelope !== "object" ||
    Array.isArray(envelope)
  ) {
    throw new EnvelopeError(
      "structure_invalid",
      `envelope must be a JSON object; got ${envelope === null ? "null" : typeof envelope}`,
    );
  }
  const e = envelope as Record<string, unknown>;
  if (e.v !== ENVELOPE_VERSION) {
    throw new EnvelopeError(
      "version_mismatch",
      `envelope version ${String(e.v)} not supported; expected ${ENVELOPE_VERSION}`,
    );
  }
  if (
    typeof e.sig !== "string" ||
    typeof e.nonce !== "string" ||
    typeof e.timestamp_ms !== "number" ||
    typeof e.origin !== "string" ||
    !("payload" in e)
  ) {
    throw new EnvelopeError(
      "structure_invalid",
      "envelope missing one or more required fields (sig, nonce, timestamp_ms, origin, payload)",
    );
  }
  if (!/^[0-9a-f]{32}$/.test(e.nonce)) {
    throw new EnvelopeError(
      "structure_invalid",
      "envelope nonce malformed; expected 32 lowercase hex chars",
    );
  }

  // Origin pin BEFORE signature check so a cross-origin attacker can't
  // probe for valid key material by trying random sigs against a
  // spoofed origin — they bounce off origin_mismatch first.
  if (e.origin !== opts.expectedOrigin) {
    throw new EnvelopeError(
      "origin_mismatch",
      `envelope origin "${e.origin}" does not match expected "${opts.expectedOrigin}"`,
    );
  }

  // Replay window. Reject both stale and future-dated envelopes.
  const now = opts.nowMs ?? Date.now();
  if (Math.abs(now - e.timestamp_ms) > REPLAY_WINDOW_MS) {
    throw new EnvelopeError(
      "replay_window",
      `envelope timestamp_ms ${e.timestamp_ms} outside ±${REPLAY_WINDOW_MS}ms window (now=${now})`,
    );
  }

  // Signature. Constant-time compare.
  const canonical = canonicalize({
    v: ENVELOPE_VERSION,
    nonce: e.nonce,
    timestamp_ms: e.timestamp_ms,
    origin: e.origin,
    payload: e.payload,
  });
  const expected = hmac(sha256, opts.key, canonical);
  let actual: Uint8Array;
  try {
    actual = base64ToBytes(e.sig);
  } catch {
    throw new EnvelopeError("sig_mismatch", "envelope sig is not valid base64");
  }
  if (actual.length !== HMAC_LEN_BYTES) {
    throw new EnvelopeError(
      "sig_mismatch",
      `envelope sig length ${actual.length} != expected ${HMAC_LEN_BYTES}`,
    );
  }
  if (!constantTimeEq(expected, actual)) {
    throw new EnvelopeError(
      "sig_mismatch",
      "envelope signature does not verify",
    );
  }

  // Replay-nonce dedup (H3) — LAST, after the frame is proven authentic and
  // in-window. Recording only authenticated nonces means a forged frame (which
  // already bounced off the sig check) can never consume a cache slot or evict a
  // legitimate entry. A nonce already accepted within the window is a replay.
  if (opts.nonceCache && !opts.nonceCache.checkAndRecord(e.origin, e.nonce, now)) {
    throw new EnvelopeError(
      "replay_nonce",
      `envelope nonce ${e.nonce} already seen within the replay window (replay attempt)`,
    );
  }

  return e.payload;
}
